import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateLines } from '@babylonjs/core/Meshes/Builders/linesBuilder';
import { Color4, Color3 } from '@babylonjs/core/Maths/math.color';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { GizmoManager } from '@babylonjs/core/Gizmos/gizmoManager';
import { ViewerEvents, waitFrames, BaseExtension } from '@immersive-tech/viewer-engine';
import { HotspotEvents } from '@/viewerExtensions/events';

import type { Viewer, Nullable } from '@immersive-tech/viewer-engine';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import type { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';

export class HotspotPicker extends BaseExtension {
  public currentPoint: [Vector3, Vector3];
  protected debugNormalLine: Nullable<LinesMesh>;
  protected debug3DPointer: Nullable<LinesMesh>;
  protected sphere: Nullable<Mesh>;
  protected enabledState: boolean;
  public hotspots: TransformNode[];
  protected gizmo: Nullable<GizmoManager>;
  protected gizmoAttachedIndex: number;

  constructor(viewer: Viewer) {
    super(viewer);

    this.currentPoint = [Vector3.Zero(), Vector3.Zero()];
    this.debugNormalLine = null;
    this.debug3DPointer = null;
    this.sphere = null;
    this.enabledState = false;
    this.hotspots = [];
    this.gizmo = null;
    this.gizmoAttachedIndex = -1;

    this.viewer.addEventListener(ViewerEvents.VIEWER_INITIALIZED, this.viewerInitializedHandler.bind(this));
  }

  // return array of box corners in local space
  public getHotspotBoxCorners() {
    return [
      // top corners
      new Vector3(-10, 10, -10),
      new Vector3(-10 / 2, 10, -10),

      new Vector3(-10, 10, -10),
      new Vector3(-10, 10 / 2, -10),

      new Vector3(-10, 10, -10),
      new Vector3(-10, 10, -10 / 2),

      new Vector3(10, 10, -10),
      new Vector3(10 / 2, 10, -10),

      new Vector3(10, 10, -10),
      new Vector3(10, 10 / 2, -10),

      new Vector3(10, 10, -10),
      new Vector3(10, 10, -10 / 2),

      new Vector3(-10, 10, 10),
      new Vector3(-10 / 2, 10, 10),

      new Vector3(-10, 10, 10),
      new Vector3(-10, 10 / 2, 10),

      new Vector3(-10, 10, 10),
      new Vector3(-10, 10, 10 / 2),

      new Vector3(10, 10, 10),
      new Vector3(10 / 2, 10, 10),

      new Vector3(10, 10, 10),
      new Vector3(10, 10 / 2, 10),

      new Vector3(10, 10, 10),
      new Vector3(10, 10, 10 / 2),

      // bottom corners
      new Vector3(-10, -10, -10),
      new Vector3(-10 / 2, -10, -10),

      new Vector3(-10, -10, -10),
      new Vector3(-10, -10 / 2, -10),

      new Vector3(-10, -10, -10),
      new Vector3(-10, -10, -10 / 2),

      new Vector3(10, -10, -10),
      new Vector3(10 / 2, -10, -10),

      new Vector3(10, -10, -10),
      new Vector3(10, -10 / 2, -10),

      new Vector3(10, -10, -10),
      new Vector3(10, -10, -10 / 2),

      new Vector3(-10, -10, 10),
      new Vector3(-10 / 2, -10, 10),

      new Vector3(-10, -10, 10),
      new Vector3(-10, -10 / 2, 10),

      new Vector3(-10, -10, 10),
      new Vector3(-10, -10, 10 / 2),

      new Vector3(10, -10, 10),
      new Vector3(10 / 2, -10, 10),

      new Vector3(10, -10, 10),
      new Vector3(10, -10 / 2, 10),

      new Vector3(10, -10, 10),
      new Vector3(10, -10, 10 / 2),
    ];
  }

  public viewerInitializedHandler() {
    if (!this.viewer.scene) return;

    // Create a line using meshbuilder
    this.debugNormalLine = CreateLines('debugNormalLine', {
      updatable: true,
      points: [Vector3.Zero(), Vector3.Zero()],
      colors: [ // Green to light green
        new Color4(0, 1, 0, 1),
        new Color4(1, 1, 0, 1),
      ],
    });

    this.debugNormalLine.isPickable = false;

    // Create a line using meshbuilder
    this.debug3DPointer = CreateLines('debug3DLine', {
      updatable: false,
      // Arrange points in 3d crosshair
      points: [
        // Forward
        Vector3.Zero(),
        new Vector3(0, 0, 0.02),

        // Right
        Vector3.Zero(),
        new Vector3(0.02, 0, 0),

        // Up
        Vector3.Zero(),
        new Vector3(0, 0.02, 0),

        // Backward
        Vector3.Zero(),
        new Vector3(0, 0, -0.02),

        // Left
        Vector3.Zero(),
        new Vector3(-0.02, 0, 0),

        // Down
        Vector3.Zero(),
        new Vector3(0, -0.02, 0),
      ],
    }, this.viewer.scene);

    this.debug3DPointer.color = new Color3(1, 0, 0);
    this.debug3DPointer.isPickable = false;

    // Create green sphere
    this.sphere = CreateSphere('sphere', {
      diameter: 0.005,
      updatable: true,
    }, this.viewer.scene);

    this.sphere.isPickable = false;

    // set the sphere to emissive color material green
    const sphereMaterial = new StandardMaterial('sphereMaterial', this.viewer.scene);

    sphereMaterial.emissiveColor = new Color3(0, 1, 0);
    this.sphere.material = sphereMaterial;

    this.viewer.scene.removeMesh(this.debug3DPointer);
    this.viewer.scene.removeMesh(this.sphere);
    this.viewer.scene.removeMesh(this.debugNormalLine);

    this.gizmo = new GizmoManager(this.viewer.scene, 0.5);
    this.gizmo.attachableNodes = [];
    this.gizmo.attachableMeshes = [];
    this.gizmo.positionGizmoEnabled = true;

    this.gizmo.onAttachedToNodeObservable.add(() => {
      this.viewer.renderNextFrame += 1;
    });
  }

  get crosshairEnabled() {
    return this.enabledState;
  }

  set crosshairEnabled(enabled: boolean) {
    if (this.enabledState === enabled || !this.debugNormalLine || !this.debug3DPointer || !this.sphere || !this.viewer.scene || !this.gizmo) return;

    if (enabled === false) {
      this.viewer.scene.removeMesh(this.debugNormalLine);
      this.viewer.scene.removeMesh(this.debug3DPointer);
      this.viewer.scene.removeMesh(this.sphere);
    } else {
      this.viewer.scene.addMesh(this.debugNormalLine);
      this.viewer.scene.addMesh(this.debug3DPointer);
      this.viewer.scene.addMesh(this.sphere);
    }

    this.enabledState = enabled;

    this.viewer.renderNextFrame += 1;
  }

  public moveDebugLine(point1: Vector3, point2: Vector3) {
    // expand point1 and point2 into a float array
    const points = [point1.x, point1.y, point1.z, point2.x, point2.y, point2.z];

    // Set line to start at point1 and end at point2
    this.debugNormalLine?.setVerticesData(
      VertexBuffer.PositionKind,
      points,
      true,
    );

    if (this.debug3DPointer) {
      // move the pointer to point1
      this.debug3DPointer.position = point1;
    }

    if (this.sphere) {
      // move the sphere to point1
      this.sphere.position = point1;
    }
  }

  public updatePicker() {
    if (!this.viewer.scene) return;

    const pickResult = this.viewer.scene.pick(this.viewer.scene.pointerX, this.viewer.scene.pointerY) as Nullable<PickingInfo>;

    if (pickResult && pickResult.hit === true) {
      const faceNormal = pickResult.getNormal(true);

      if (faceNormal && pickResult.pickedPoint) {
        // clone the picked point and translate 5 units from the picked point in the normal direction
        const pickedPointEndofLine = pickResult.pickedPoint.clone();

        pickedPointEndofLine.addInPlace(faceNormal.scale(.028)); // translate 5 units from the picked point in the normal direction

        this.currentPoint = [pickResult.pickedPoint, pickedPointEndofLine];
        this.moveDebugLine(pickResult.pickedPoint, pickedPointEndofLine);
      }
    }
  }

  public createHotspotPlacer(position: Vector3)  {
    const transformNode = new TransformNode('hotspotPlacer');

    transformNode.position.set(position.x, position.y, position.z);

    this.hotspots.push(transformNode);
    this.gizmo?.attachableNodes?.push(transformNode);

    transformNode.onAfterWorldMatrixUpdateObservable.add(() => {
      const attachedHotspot = this.hotspots[this.gizmoAttachedIndex];

      if (attachedHotspot !== transformNode) return;

      this.viewer.dispatchViewerEvent(HotspotEvents.GIZMO_MOVE_HOTSPOT, {
        targetIndex: this.gizmoAttachedIndex,
        position: attachedHotspot.position,
      });
    });

    return this.hotspots.length - 1;
  }

  public moveHotspotPlacer(placerIndex: number, position: Vector3) {
    const hotspot = this.hotspots[placerIndex];

    if (!hotspot) return;

    hotspot.position.set(position.x, position.y, position.z);

    this.viewer.renderNextFrame += 1;
  }

  public destroyHotspotPlacer(placerIndex: number) {
    this.hotspots[placerIndex].dispose();

    this.hotspots.splice(placerIndex, 1);
  }

  public destroyAllHotspotPlacers() {
    for (let index = this.hotspots.length - 1; index >= 0; index--) {
      this.destroyHotspotPlacer(index);
    }
  }

  public attachGizmoToHotspot(hotspotIndex: number) {
    if (!this.gizmo || hotspotIndex < 0 || !this.hotspots[hotspotIndex]) return;
    if (this.gizmoAttachedIndex === hotspotIndex) return;

    const hadAttachedHotspot = this.gizmoAttachedIndex !== -1;

    this.gizmo.attachToNode(this.hotspots[hotspotIndex]);

    this.gizmoAttachedIndex = hotspotIndex;

    if (!hadAttachedHotspot) {
      this.viewer.renderSemaphore += 1;
    }

    this.viewer.renderNextFrame += 1;
  }

  public async detachGizmo() {
    if (!this.gizmo || !this.viewer.scene || this.gizmoAttachedIndex === -1) return;

    this.gizmo.attachToNode(null);

    this.gizmoAttachedIndex = -1;

    this.viewer.renderSemaphore -= 1;

    await waitFrames(1, this.viewer.scene)

    this.viewer.renderNextFrame += 1;
  }
}
